/**
 * Environment types.
 *
 * An Environment is a first-class reusable creative asset in Pixle: a
 * persistent location that can be reused across videos and scenes.
 * (Environment = reusable place/location. Scene = a unit of a video/story.)
 */

export interface EnvironmentReferenceImage {
  id: string;
  /**
   * Stored blob location (e.g. `users/<userId>/assets/foo.png`) or a direct
   * URL (https/data/blob) for legacy entries.
   */
  url: string;
  /**
   * User-editable label for the reference image (e.g. "Wide angle",
   * "Character's bed"). Defaults to the uploaded file name.
   */
  name: string;
  width: number;
  height: number;
}

export interface EnvironmentIdentity {
  name?: string;
  description?: string;
  tags?: string[];
}

export interface Environment {
  id: string;
  name: string;
  description?: string;
  /**
   * Extensible structured detail bag (mirrors the character `identity`
   * pattern). Flat `name`/`description` stay canonical for display.
   */
  identity?: EnvironmentIdentity;
  /** 1–3 reference images of the same environment. */
  referenceImages: EnvironmentReferenceImage[];
  createdAt: string;
  updatedAt: string;
}

/** Maximum reference images per environment. */
export const MAX_ENVIRONMENT_REFERENCE_IMAGES = 3;

/** Maximum user-editable reference-image label length. */
export const MAX_ENVIRONMENT_IMAGE_LABEL_LENGTH = 80;

/**
 * Environment snapshot sent to generation endpoints (by value, mirroring
 * the character reference payloads). Reference image URLs must already be
 * resolved (signed HTTPS URLs, like characters[].image) — the backend
 * feeds them to the model as image references as-is.
 */
export interface EnvironmentSnapshot {
  id?: string;
  name?: string;
  description?: string;
  referenceImages?: { url: string; name?: string }[];
}
